import { useApiMutation } from "@gladis/api-client";
import { Button, Dialog, Label, MoneyInput, TextField, parseEurInput } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { SAVINGS_KEY } from "./AccountList.js";

/**
 * Record a new balance for an account (FR-009 to FR-016).
 *
 * IT ONLY EVER APPENDS. There is no edit affordance anywhere in this module, because there is no
 * edit: a wrong figure is corrected by entering the right one, and the wrong one stays in the
 * history where it can be seen. That is not a limitation being worked around — it is what makes the
 * chart a reconstruction rather than a record of what somebody last decided to display.
 *
 * IT IS A MODAL, and it was an inline panel until 2026-08-21 — the LAST form in the application
 * that was not. `AccountForm` beside it gives the reason and this form had it worse: the panel was
 * reached from a row in the accounts table at the BOTTOM of the page and opened at the TOP of it,
 * so the click that said "update this balance" scrolled the form out of view and pushed the stat
 * cards, the chart and the row that was just clicked down by its own height. Reported as "a modal
 * doesn't open, a form shows inline at the top of the page", which is exactly what it did.
 *
 * THE HEADING AND THE SENTENCE ARE `Dialog` PROPS, so the frame and the `<h2>` this file used to
 * draw are the dialog's now. The heading was also the last hard-coded English sentence in a domain
 * package's interface — `Update the balance of {accountName}` — which `no-untranslated-literals`
 * could not see, because its JSX-text pattern stops at a `{`.
 *
 * THE FUTURE-DATE WARNING IS THE ONLY WARNING THIS MODULE RAISES.
 *
 * Backdating is silent, deliberately: `savings.md` calls it "the supported mechanism for
 * backfilling" and AC-S8 requires no warning. Warning about the normal case would train people to
 * dismiss the warning, and then the one that matters goes with it.
 */
export function RecordBalanceForm({
  open,
  onClose,
  accountId,
  accountName,
  onRecorded,
}: {
  open: boolean;
  onClose: () => void;
  accountId: string;
  /**
   * The account's name, when the caller knows it.
   *
   * Omitted by the account's own page, where the name is already the heading above and the dialog
   * would be repeating it — that page passed the literal `"this account"` before, which is a
   * sentence fragment in one language for a reader who may be in the other.
   */
  accountName?: string | undefined;
  onRecorded?: () => void;
}): ReactNode {
  const t = useT();
  const [balance, setBalance] = useState("");
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10));

  /*
   * WHICH ACCOUNT THIS DIALOG IS ABOUT, CAPTURED WHEN IT OPENS.
   *
   * The account comes from the row that was clicked, and the caller drops that selection the moment
   * it closes — so between the state update and the effect that calls `close()` this component
   * renders once with no account in hand. `useEffect` runs after paint, so that render can reach the
   * screen: a title reading "Update the balance of" with nothing after it, for a frame.
   *
   * Holding it in state means the dialog keeps saying what it was about until it is gone. It is the
   * same reason `CardForm` fills its fields from `editing` only at open and reads its own state
   * afterwards.
   */
  const [target, setTarget] = useState({ id: accountId, name: accountName });

  /*
   * THE FIELDS ARE CLEARED WHEN THE DIALOG OPENS, and that is a consequence of it staying mounted.
   *
   * A native `<dialog>` cannot be conditionally rendered — `Dialog` calls `showModal()` from an
   * effect on an element it already holds a ref to, and an element appearing in the same commit as
   * `open` has no first render in which to be closed. So this component no longer unmounts between
   * uses, and without this its `useState` would hold the LAST balance somebody typed: opening the
   * form for a second account carrying the first account's figure, which is the defect 016 records
   * for the investments dialogs.
   */
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTarget({ id: accountId, name: accountName });
      setBalance("");
      setAsOfDate(new Date().toISOString().slice(0, 10));
    }
  }

  const record = useApiMutation<{ id: string }, { balance: number; asOfDate: string }>(
    (client, input) =>
      client.post<{ id: string }>(`/savings/accounts/${target.id}/balances`, input),
    { invalidates: [SAVINGS_KEY] },
  );

  // Compared as ISO STRINGS, which sort lexicographically. Two `Date`s would put the browser's
  // timezone into a question that has none in it.
  const isFuture = asOfDate > new Date().toISOString().slice(0, 10);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const amount = parseEurInput(balance);
    if (amount === null) return;

    record.mutate(
      { balance: amount, asOfDate },
      {
        onSuccess: () => {
          setBalance("");
          onRecorded?.();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        target.name === undefined
          ? t("savings.record.heading")
          : t("savings.record.headingNamed", { name: target.name })
      }
      description={t("savings.record.hint")}
    >
      <form onSubmit={submit}>
        <div className="grid gap-4">
          <div>
            <Label htmlFor="record-balance">{t("savings.record.newBalance")}</Label>
            <MoneyInput
              id="record-balance"
              value={balance}
              onValueChange={setBalance}
              placeholder="0,00"
              required
            />
          </div>

          <div>
            <Label htmlFor="record-date">{t("savings.record.asOf")}</Label>
            <TextField
              id="record-date"
              type="date"
              value={asOfDate}
              onChange={(event) => setAsOfDate(event.target.value)}
              required
            />
            {isFuture ? (
              <p
                className="mt-2 text-sm text-muted"
                role="status"
                data-testid="future-date-warning"
              >
                {t("savings.form.futureDate")}
              </p>
            ) : null}
          </div>

          {record.isError ? (
            <p className="text-sm text-muted" role="alert">
              {errorSentence(record.error)}
            </p>
          ) : null}
        </div>

        {/* Cancel beside Submit — see the note in `CardForm`: the three other ways out of a dialog
            are all invisible. */}
        <div className="mt-5 flex justify-end gap-3">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={record.isPending}>
            {record.isPending ? t("savings.record.submitting") : t("savings.record.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
